import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Search, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TransferForm from "./TransferForm";
import TransferViewModal from "./TransferViewModal";
import { deleteTransfer, fetchTransfers as fetchTransfersApi, getTransfer, type Transfer, type TransferListItem } from "@/lib/transfersApi";

type SortField = "title" | "emirate" | "type" | null;
type SortOrder = "asc" | "desc";

const TransfersList = () => {
  const { userRole } = useAuth();
  const [transfers, setTransfers] = useState<TransferListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState<number | null>(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const { toast } = useToast();
  
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      
      const response = await fetchTransfersApi({
        page: currentPage,
        size: pageSize,
        search: debouncedSearchTerm,
        sortField: sortField ?? undefined,
        sortOrder,
      });

      const items = Array.isArray(response?.items) ? response.items : [];
      const total = typeof response?.total === "number" ? response.total : items.length;
      setTransfers(items);
      setTotalCount(total);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransfers();
  }, [debouncedSearchTerm, currentPage, pageSize, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortField(null);
        setSortOrder("asc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleView = (transferId: number) => {
    setSelectedTransferId(transferId);
    setViewModalOpen(true);
  };

  const handleEdit = async (transfer: TransferListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const data = await getTransfer(transfer.id);
      setSelectedTransfer(data);
      setIsFormOpen(true);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteTransfer(deleteId);
      toast({ title: "Success", description: "Transfer deleted successfully" });
      loadTransfers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const canEdit = Boolean(userRole);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-foreground">Transfers</h2>
        {canEdit && (
          <Button onClick={() => { setSelectedTransfer(null); setIsFormOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Transfer
          </Button>
        )}
      </div>

      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search by code, emirate, or title..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="pl-10"
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("emirate")}
                  className="h-8 px-2"
                >
                  Emirate
                  <ArrowUpDown 
                    className={`ml-2 h-4 w-4 ${sortField === "emirate" ? "text-primary" : ""}`} 
                  />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("title")}
                  className="h-8 px-2"
                >
                  Title
                  <ArrowUpDown 
                    className={`ml-2 h-4 w-4 ${sortField === "title" ? "text-primary" : ""}`} 
                  />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("type")}
                  className="h-8 px-2"
                >
                  Type
                  <ArrowUpDown 
                    className={`ml-2 h-4 w-4 ${sortField === "type" ? "text-primary" : ""}`} 
                  />
                </Button>
              </TableHead>
              <TableHead>Capacity</TableHead>
              {canEdit && (
                <TableHead className="text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 6 : 5} className="text-center text-muted-foreground h-24">
                  Loading transfers...
                </TableCell>
              </TableRow>
            ) : transfers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 6 : 5} className="text-center text-muted-foreground h-24">
                  No transfers found
                </TableCell>
              </TableRow>
            ) : (
              transfers.map((transfer) => (
                <TableRow 
                  key={transfer.id}
                  onClick={() => handleView(transfer.id)}
                  className="cursor-pointer"
                >
                  <TableCell className="font-medium">{transfer.transfer_code}</TableCell>
                  <TableCell>{transfer.emirate}</TableCell>
                  <TableCell>{transfer.title}</TableCell>
                  <TableCell className="capitalize">{transfer.type}</TableCell>
                  <TableCell>{transfer.capacity || "-"}</TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleEdit(transfer, e)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(transfer.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="text-sm">
            Page {currentPage} of {totalPages || 1}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isFormOpen && (
        <TransferForm
          open={isFormOpen}
          onOpenChange={(open) => {
            if (!open) {
              setIsFormOpen(false);
              setSelectedTransfer(null);
            }
          }}
          initialData={selectedTransfer}
          onSuccess={() => {
            setIsFormOpen(false);
            setSelectedTransfer(null);
            loadTransfers();
          }}
        />
      )}

      {viewModalOpen && selectedTransferId && (
        <TransferViewModal
          transferId={selectedTransferId}
          open={viewModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              setViewModalOpen(false);
              setSelectedTransferId(null);
            }
          }}
        />
      )}

      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the transfer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TransfersList;
